import { createRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMe } from '../auth';
import { useCase } from '../cases';
import { ArtifactsPane } from '../components/artifacts-pane';
import { CaseDataPane } from '../components/case-data-pane';
import { CaseMetaStrip } from '../components/case-meta-strip';
import { CaseSituation } from '../components/case-situation';
import { ChatPaneShell } from '../components/chat-pane';
import { FilesPanel } from '../components/files-panel';
import { IntakeView } from '../components/intake-view';
import { TimelinePane } from '../components/timeline-pane';
import { useFiles } from '../files';
import { useLocale } from '../locale';
import { m } from '../paraglide/messages';
import { rootRoute } from './root';

export const caseDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cases/$id',
  component: CaseDetailRoute,
  validateSearch: (s: Record<string, unknown>) => ({
    /** Deep-link target for the 24h follow-up email's CTA. When `log=outcome`
     * the artifacts tab is selected on mount and the LogOutcome modal
     * auto-opens. The route clears the param after the modal closes. */
    log: s.log === 'outcome' ? 'outcome' : undefined,
  }),
});

function CaseDetailRoute() {
  const { id } = caseDetailRoute.useParams();
  const search = caseDetailRoute.useSearch();
  const me = useMe();
  const navigate = useNavigate();
  const c = useCase(id);
  // Subscribe so the whole tree re-renders when the locale toggles.
  // (Children read paraglide's `m.*` directly; without subscribing here,
  // the language switch wouldn't repaint case-detail.)
  useLocale();

  useEffect(() => {
    if (!me.isLoading && !me.data?.user) {
      navigate({ to: '/login', replace: true });
    }
  }, [me.isLoading, me.data, navigate]);

  if (c.isLoading) return <p className="centered muted">{m.caseLoading()}</p>;
  if (c.isError) return <p className="centered error">{m.caseNotFound()}</p>;
  if (!c.data) return null;

  // File-first intake gate. Until the user has explicitly finished the upload
  // stage (or has zero files and clicked "skip to chat"), we hide the chat
  // entirely and present a single-column, upload-focused view. This matches
  // the product flow: files → AI extraction → chat fills gaps.
  if (!c.data.intake_done_at) {
    return <IntakeView caseId={c.data.id} />;
  }

  return (
    <div className="case-shell">
      <aside className="chat-pane">
        <ChatPaneShell caseId={c.data.id} />
      </aside>
      <section className="main-pane">
        <header className="case-head">
          {/* Pure orientation. Title + meta (phase · €paid · started),
           *  then ONE positive nudge for the next thing the user can do
           *  to make their case stronger. The nudge sits quiet when
           *  there's nothing to suggest. The chronology check resurfaces
           *  here only when the timeline actually has a contradiction. */}
          <h1 className="case-head-title">{c.data.title ?? m.casesListUntitled()}</h1>
          <CaseMetaStrip caseId={c.data.id} status={c.data.status} startedAt={c.data.created_at} />
          <CaseSituationHost caseId={c.data.id} />
        </header>
        <CaseTabs
          caseId={c.data.id}
          deepLinkLogOutcome={search.log === 'outcome'}
          onClearDeepLink={() =>
            navigate({
              to: '/cases/$id',
              params: { id: c.data?.id ?? '' },
              search: { log: undefined },
              replace: true,
            })
          }
        />
      </section>
    </div>
  );
}

type TabKey = 'timeline' | 'data' | 'sources' | 'artifacts';

// Lifts the "switch to tab X" callback up to <CaseSituation> without
// making the situation prompt know about the tab implementation. The
// situation emits a custom DOM event the tabs container listens for —
// same wiring TimelinePane uses to send the user to Sources.
function CaseSituationHost({ caseId }: { caseId: string }) {
  return (
    <CaseSituation
      caseId={caseId}
      onJumpToTab={(tab) => {
        const ev = new CustomEvent('claimpack:switch-tab', { detail: { tab } });
        window.dispatchEvent(ev);
      }}
    />
  );
}

function CaseTabs({
  caseId,
  deepLinkLogOutcome = false,
  onClearDeepLink,
}: {
  caseId: string;
  deepLinkLogOutcome?: boolean;
  onClearDeepLink?: () => void;
}) {
  // Land on artifacts when arriving via the follow-up email's deep link;
  // otherwise default to timeline like before.
  const [tab, setTab] = useState<TabKey>(deepLinkLogOutcome ? 'artifacts' : 'timeline');
  const files = useFiles(caseId);
  const fileCount = files.data?.length ?? 0;

  // Listen for cross-component "switch to tab X" events. The header's
  // <CaseSituation> dispatches them so clicking the next-best-action
  // nudge can land the user on the relevant tab.
  //
  // The event detail can also carry a `target`: an instruction the
  // landing pane uses to scroll to / focus / flash the relevant element
  // (a specific fact field on the Data tab, all proof-missing events on
  // the Timeline tab, …). After switching the tab we re-dispatch the
  // target as `claimpack:reveal-target` on the next frame so the new
  // pane has mounted before its handler fires. Each pane ignores
  // targets it doesn't recognise.
  useEffect(() => {
    function onSwitch(e: Event) {
      const detail = (e as CustomEvent<{ tab: TabKey; target?: unknown }>).detail;
      if (!detail) return;
      if (
        detail.tab === 'timeline' ||
        detail.tab === 'data' ||
        detail.tab === 'sources' ||
        detail.tab === 'artifacts'
      ) {
        setTab(detail.tab);
        if (detail.target) {
          requestAnimationFrame(() => {
            window.dispatchEvent(
              new CustomEvent('claimpack:reveal-target', { detail: detail.target }),
            );
          });
        }
      }
    }
    window.addEventListener('claimpack:switch-tab', onSwitch);
    return () => window.removeEventListener('claimpack:switch-tab', onSwitch);
  }, []);

  return (
    <>
      <div className="timeline-tabs">
        <button
          type="button"
          className={`tab ${tab === 'timeline' ? 'active' : ''}`}
          onClick={() => setTab('timeline')}
        >
          {m.tabTimeline()}
        </button>
        <button
          type="button"
          className={`tab ${tab === 'data' ? 'active' : ''}`}
          onClick={() => setTab('data')}
        >
          {m.tabData()}
        </button>
        <button
          type="button"
          className={`tab ${tab === 'sources' ? 'active' : ''}`}
          onClick={() => setTab('sources')}
        >
          {m.tabSources()}
          {fileCount > 0 ? ` · ${fileCount}` : ''}
        </button>
        <button
          type="button"
          className={`tab ${tab === 'artifacts' ? 'active' : ''}`}
          onClick={() => setTab('artifacts')}
        >
          {m.tabArtifacts()}
        </button>
      </div>
      {tab === 'timeline' ? <TimelinePane caseId={caseId} /> : null}
      {tab === 'data' ? <CaseDataPane caseId={caseId} /> : null}
      {tab === 'sources' ? <FilesPanel caseId={caseId} /> : null}
      {tab === 'artifacts' ? (
        <ArtifactsPane
          caseId={caseId}
          forceOpenLogOutcome={deepLinkLogOutcome}
          onCloseForceOpenLogOutcome={onClearDeepLink}
        />
      ) : null}
    </>
  );
}
